/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import { getPromotionSkips } from 'services/api/devicesApi';
import { TPromotionSkipsState, TPromotionSkipType, TProductType } from 'typings';

// Single source of truth for whether this space has already seen the
// post-pairing promotion for a product type (FCM-2832 follow-up), replacing
// the devices/deleted-based heuristic in AddCameraInfoV1. Scoped to the
// product type being paired, since a space can have paid HomeSecurity
// cameras while still being first-time for a different product (e.g. Bird).
export const fetchPromotionSkips = createAsyncThunk(
  'spaces/fetchPromotionSkips',
  async (
    { spaceId, productType }: { spaceId: string; productType: TProductType },
    thunkAPI
  ) => {
    try {
      const response = await getPromotionSkips(spaceId, productType);
      return (response.data?.items ?? []) as TPromotionSkipType[];
    } catch (error) {
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchPromotionSkips',
          error_type: 'api_error'
        },
        extra: { spaceId, productType }
      });
      return thunkAPI.rejectWithValue('failed');
    }
  }
);

const initialPromotionSkipsState: TPromotionSkipsState = {
  promotionSkips: [],
  isLoading: false
};

export const promotionSkipsSlice = createSlice({
  name: 'promotionSkips',
  initialState: initialPromotionSkipsState,
  reducers: {
    clearPromotionSkips: (state) => {
      state.promotionSkips = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromotionSkips.fulfilled, (state, action) => {
        state.isLoading = false;
        state.promotionSkips = action.payload;
      })
      .addCase(fetchPromotionSkips.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPromotionSkips.rejected, (state) => {
        state.isLoading = false;
        state.promotionSkips = [];
      });
  }
});

export const { clearPromotionSkips } = promotionSkipsSlice.actions;

export default promotionSkipsSlice.reducer;
